import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import BrandMark from './BrandMark';
import './SiteHeader.css';
import SkinPicker from './SkinPicker';
import ThemeIcon from './ThemeIcon';

function SiteHeader({ skin = 'regular', period = 'day', onSkinChange = () => {} }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const header = useRef(null);
  useLayoutEffect(() => {
    const element = header.current;
    const app = element?.closest('.public-app');
    if (!app) return undefined;
    // The reading rail and anchor offsets follow the actual navigation height,
    // including font loading, zoom and responsive skin changes.
    const measure = () => {
      const height = `${Math.ceil(element.getBoundingClientRect().height)}px`;
      if (app.style.getPropertyValue('--site-header-height') !== height) {
        app.style.setProperty('--site-header-height', height);
      }
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      app.style.removeProperty('--site-header-height');
    };
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = event => {
      if (!header.current?.contains(event.target)) setOpen(false);
    };
    const keydown = event => {
      if (event.key === 'Escape') {
        setOpen(false); trigger.current?.focus();
      }
    };
    const viewport = window.matchMedia('(min-width: 769px)');
    const resized = () => { if (viewport.matches) setOpen(false); };
    document.addEventListener('pointerdown', dismiss, { passive: true });
    document.addEventListener('keydown', keydown);
    viewport.addEventListener?.('change', resized);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', keydown);
      viewport.removeEventListener?.('change', resized);
    };
  }, [open]);
  const close = () => setOpen(false);
  const items = [['/', '首页', 'home'], ['/blog', '博客', 'journal'], ['/archives', '归档', 'archive'], ['/guestbook', '留言板', 'message'], ['/about', '关于', 'about']];
  return <>
    <header className="header" ref={header}>
      <div className="header-content">
        <h1 className="logo"><Link to="/" aria-label="双霸服的霸服窝，返回首页" onClick={close}><BrandMark skin={skin} period={period} /><span>双霸服的霸服窝<small>A LITTLE SPACE FOR BIG IDEAS</small></span></Link></h1>
        <button type="button" className="mobile-menu-btn" ref={trigger} onClick={() => setOpen(value => !value)} aria-label={open ? '关闭导航菜单' : '打开导航菜单'} aria-expanded={open} aria-controls="site-navigation"><span className={`hamburger ${open ? 'active' : ''}`} /></button>
        <nav id="site-navigation" className={`nav ${open ? 'active' : ''}`} aria-label="主导航">
          <div className="nav-content">
            {items.map(([path, label, icon]) => <NavLink key={path} to={path} className={({ isActive }) => `nav-link ${isActive || (path === '/blog' && pathname.startsWith('/post/')) ? 'active' : ''}`} onClick={close}>{skin !== 'regular' && <ThemeIcon name={icon} skin={skin} period={period} />}<span>{label}</span></NavLink>)}
            <SkinPicker skin={skin} period={period} onSkinChange={onSkinChange} onSelect={close} />
            <Link to={localStorage.getItem('isAdmin') ? '/admin/dashboard' : '/admin/login'} className="nav-link" onClick={close} target="_blank" rel="noopener noreferrer">{skin !== 'regular' && <ThemeIcon name={localStorage.getItem('isAdmin') ? 'workspace' : 'key'} variant="action" skin={skin} period={period} />}<span>{localStorage.getItem('isAdmin') ? '管理' : '登录'}</span></Link>
          </div>
        </nav>
      </div>
    </header>
    {open && <button type="button" className="mobile-menu-dismiss" aria-label="点击空白处关闭导航菜单" onClick={close} tabIndex={-1} />}
  </>;
}

export default memo(SiteHeader);
